import { createFileRoute, Link } from '@tanstack/react-router';
import { useAgingReceivables } from '@/hooks/use-reports';
import { PageHeader } from '@/components/page-header';
import { MainLayout } from '@/components/main-layout';
import { ProtectedRoute } from '@/components/protected-route';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Download, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/reports/aging-receivables')({
  component: AgingReceivablesPage,
});

function AgingReceivablesPage() {
  const { data: agingData, isLoading, error } = useAgingReceivables();

  const formatCurrency = (amount: number | string) => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(isNaN(val) ? 0 : val);
  };

  const formatDate = (dateStr: string | Date | null) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return '-';
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive opacity-50" />
        <p className="text-muted-foreground">Failed to load aging report</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  const buckets = [
    { id: '0_30', label: '0-30 Days', data: agingData?.['0_30'] || [], color: 'bg-blue-500/10 text-blue-600' },
    { id: '31_60', label: '31-60 Days', data: agingData?.['31_60'] || [], color: 'bg-amber-500/10 text-amber-600' },
    { id: '61_90', label: '61-90 Days', data: agingData?.['61_90'] || [], color: 'bg-orange-500/10 text-orange-600' },
    { id: '90_plus', label: '90+ Days', data: agingData?.['90_plus'] || [], color: 'bg-destructive/10 text-destructive' },
  ];

  const allInvoices = buckets.flatMap(b => b.data);
  const totalDue = allInvoices.reduce((sum, inv) => sum + inv.amountDue, 0);

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-center gap-2 mb-2">
        <Link to="/reports">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Reports
          </Button>
        </Link>
      </div>

      <PageHeader
        title="Aging Receivables Report"
        description="Comprehensive list of all outstanding customer debts grouped by age."
        actions={(
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {buckets.map((bucket) => {
          const bucketTotal = bucket.data.reduce((sum, inv) => sum + inv.amountDue, 0);
          return (
            <Card key={bucket.id} className="relative overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  {bucket.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={cn("text-2xl font-bold", bucket.id === '90_plus' && "text-destructive")}>
                  {formatCurrency(bucketTotal)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {bucket.data.length} invoice{bucket.data.length !== 1 ? 's' : ''}
                </p>
              </CardContent>
              <div className={cn("absolute bottom-0 left-0 h-1 w-full", bucket.color.split(' ')[0])} />
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Detailed List</CardTitle>
              <CardDescription>All unpaid and partially paid invoices</CardDescription>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase">Total Receivables</p>
              <p className="text-xl font-bold text-primary">{formatCurrency(totalDue)}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="pl-6">Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Age (Days)</TableHead>
                <TableHead className="text-right">Amount Due</TableHead>
                <TableHead className="pr-6 text-right">Bucket</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No outstanding receivables found. Great job!
                  </TableCell>
                </TableRow>
              ) : (
                allInvoices.sort((a, b) => b.ageDays - a.ageDays).map((inv) => {
                  let bucketLabel = "0-30";
                  let bucketColor = "bg-blue-500/10 text-blue-600 border-blue-200";
                  if (inv.ageDays > 90) { bucketLabel = "90+"; bucketColor = "bg-destructive/10 text-destructive border-destructive/20"; }
                  else if (inv.ageDays > 60) { bucketLabel = "61-90"; bucketColor = "bg-orange-500/10 text-orange-600 border-orange-200"; }
                  else if (inv.ageDays > 30) { bucketLabel = "31-60"; bucketColor = "bg-amber-500/10 text-amber-600 border-amber-200"; }
                  
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="font-bold pl-6">#{inv.invoiceNo}</TableCell>
                      <TableCell>{inv.customerName}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(inv.date)}</TableCell>
                      <TableCell className="text-right font-mono">{inv.ageDays}</TableCell>
                      <TableCell className="text-right font-bold text-primary">{formatCurrency(inv.amountDue)}</TableCell>
                      <TableCell className="pr-6 text-right">
                        <Badge variant="outline" className={cn("text-[10px] font-bold uppercase", bucketColor)}>
                          {bucketLabel} Days
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
